import React, { useDeferredValue, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import TuneIcon from '@mui/icons-material/Tune';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AutomationStatusCards from '../components/automation/AutomationStatusCards';
import AutomationFlowCard from '../components/automation/AutomationFlowCard';
import AutomationJobsTable from '../components/automation/AutomationJobsTable';
import AutomationSkeleton from '../components/automation/AutomationSkeleton';
import { automationAPI } from '../services/api';
import { colors, status } from '../theme';

const CATEGORY_ORDER = ['morning', 'intraday', 'market_close', 'evening', 'recurring'];

function AutomationCenter() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [feedback, setFeedback] = useState(null);
  const deferredSearch = useDeferredValue(searchValue.trim().toLowerCase());

  const dashboardQuery = useQuery({
    queryKey: ['automation-dashboard'],
    queryFn: async () => {
      const response = await automationAPI.getDashboard();
      return response.data;
    },
  });

  const flowMutation = useMutation({
    mutationFn: (flowKey) => automationAPI.triggerFlow(flowKey),
    onSuccess: async (response) => {
      setFeedback({ severity: 'success', message: response.data.message });
      await queryClient.invalidateQueries({ queryKey: ['automation-dashboard'] });
    },
    onError: () => {
      setFeedback({ severity: 'error', message: t('automation.error.flow', 'Unable to queue automation flow.') });
    },
  });

  const jobMutation = useMutation({
    mutationFn: (jobKey) => automationAPI.triggerJob(jobKey),
    onSuccess: async (response) => {
      setFeedback({ severity: 'success', message: response.data.message });
      await queryClient.invalidateQueries({ queryKey: ['automation-dashboard'] });
    },
    onError: () => {
      setFeedback({ severity: 'error', message: t('automation.error.job', 'Unable to queue automation job.') });
    },
  });

  const jobs = useMemo(() => dashboardQuery.data?.jobs || [], [dashboardQuery.data]);

  const categoryGroups = useMemo(() => {
    const groups = new Map();
    jobs.forEach((job) => {
      const key = job.category_key || 'other';
      if (!groups.has(key)) {
        groups.set(key, { key, label: job.category_label || key, count: 0 });
      }
      groups.get(key).count += 1;
    });
    const sorted = Array.from(groups.values()).sort((a, b) => {
      const ai = CATEGORY_ORDER.indexOf(a.key);
      const bi = CATEGORY_ORDER.indexOf(b.key);
      if (ai === -1 && bi === -1) return a.label.localeCompare(b.label);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
    return sorted;
  }, [jobs]);

  const filteredJobs = useMemo(() => {
    let result = jobs;
    if (categoryFilter !== 'all') {
      result = result.filter((job) => (job.category_key || 'other') === categoryFilter);
    }
    if (deferredSearch) {
      result = result.filter((job) => {
        const haystack = [job.key, job.title, job.task_name, job.category_label, job.schedule_label]
          .join(' ')
          .toLowerCase();
        return haystack.includes(deferredSearch);
      });
    }
    return result;
  }, [jobs, categoryFilter, deferredSearch]);

  if (dashboardQuery.isLoading) {
    return <AutomationSkeleton />;
  }

  if (dashboardQuery.isError || !dashboardQuery.data) {
    return (
      <Alert severity="error">
        {t('automation.error.load', 'Unable to load automation dashboard.')}
      </Alert>
    );
  }

  const { summary, flows } = dashboardQuery.data;
  const generatedAt = summary.generated_at
    ? new Date(summary.generated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <Box sx={{ width: '100%', px: { xs: 1.5, md: 2 }, py: 2 }}>
      <Stack spacing={1.5}>
        {/* ─── Header (hedge-style) ─── */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', md: 'flex-start' },
            flexDirection: { xs: 'column', md: 'row' },
            gap: 1.5,
            mb: 0.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '12px',
                flexShrink: 0,
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 3px 10px rgba(37, 99, 235, 0.25)',
              }}
            >
              <TuneIcon sx={{ fontSize: 22, color: '#fff' }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: '#0f172a',
                    fontSize: '1.2rem',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {t('automation.title', 'Automation Center')}
                </Typography>
                {generatedAt && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.6,
                      bgcolor: '#f1f5f9',
                      px: 1,
                      py: 0.3,
                      borderRadius: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: summary.worker_online ? '#10b981' : '#f59e0b',
                        boxShadow: summary.worker_online
                          ? '0 0 6px #10b981'
                          : '0 0 6px #f59e0b',
                      }}
                    />
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>
                      {generatedAt}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.15 }}>
                {t(
                  'automation.subtitle.short',
                  'Celery Beat jobs, worker state, and one-click recovery flows'
                )}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
            <TextField
              size="small"
              placeholder={t('automation.jobs.search', 'Filter jobs')}
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                  </InputAdornment>
                ),
                endAdornment: searchValue ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchValue('')} sx={{ p: 0.25 }}>
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                width: { xs: '100%', md: 240 },
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#fff',
                  borderRadius: '4px',
                  fontSize: 13.5,
                  height: 36,
                  '& fieldset': { borderColor: '#e2e8f0' },
                  '&:hover fieldset': { borderColor: '#cbd5e1' },
                },
              }}
            />
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => dashboardQuery.refetch()}
              disabled={dashboardQuery.isFetching}
              sx={{
                textTransform: 'none',
                fontSize: 13,
                fontWeight: 600,
                borderRadius: '4px',
                color: '#64748b',
                borderColor: '#e2e8f0',
                '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' },
                '& .MuiButton-startIcon': { mr: 0.5 },
              }}
            >
              {dashboardQuery.isFetching
                ? t('automation.refreshing', 'Refreshing…')
                : t('automation.refresh', 'Refresh')}
            </Button>
          </Box>
        </Box>

        {/* ─── Status strip ─── */}
        <AutomationStatusCards summary={summary} t={t} />

        {/* Worker offline warning */}
        {!summary.worker_online && (
          <Alert severity="warning" sx={{ borderRadius: 2, py: 0.5, fontSize: 13 }}>
            {t(
              'automation.warning.worker',
              'Celery worker is currently offline. You can still inspect the schedule, but trigger actions may fail until the worker container is running.'
            )}
          </Alert>
        )}

        {/* Feedback */}
        {feedback && (
          <Alert
            severity={feedback.severity}
            onClose={() => setFeedback(null)}
            sx={{ borderRadius: 2, py: 0.5, fontSize: 13 }}
          >
            {feedback.message}
          </Alert>
        )}

        {/* ─── Flows row (horizontal, full-width) ─── */}
        <Box>
          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.85, px: 0.25 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: 14, color: colors.gray[800] }}>
              {t('automation.flows.title', 'Operator Flows')}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.gray[500], fontSize: 11.5 }}>
              {t('automation.flows.subtitle.short', 'Curated entry points for daily and recovery automation')}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" sx={{ color: colors.gray[500], fontSize: 11.5, fontWeight: 600 }}>
              {t('automation.flows.count', '{{count}} flows', { count: flows.length })}
            </Typography>
          </Stack>
          <Box
            sx={{
              display: 'grid',
              gap: 1.25,
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(auto-fit, minmax(280px, 1fr))',
              },
            }}
          >
            {flows.map((flow) => (
              <AutomationFlowCard
                key={flow.key}
                flow={flow}
                onTrigger={(flowKey) => flowMutation.mutate(flowKey)}
                isTriggering={flowMutation.isPending && flowMutation.variables === flow.key}
                t={t}
              />
            ))}
          </Box>
        </Box>

        {/* ─── Jobs section ─── */}
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            borderColor: colors.gray[200],
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 1.5,
              pt: 1.25,
              pb: 0,
              borderBottom: `1px solid ${colors.gray[200]}`,
              bgcolor: colors.gray[50],
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: 14, color: colors.gray[800] }}>
                {t('automation.jobs.title', 'Scheduled Jobs')}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.gray[500], fontSize: 11.5 }}>
                {t(
                  'automation.jobs.subtitle.short',
                  'Manual trigger stays available even when a Beat job is disabled'
                )}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Typography variant="caption" sx={{ color: colors.gray[500], fontSize: 11.5, fontWeight: 600 }}>
                {t('automation.jobs.filtered', '{{visible}} / {{total}} jobs visible', {
                  visible: filteredJobs.length,
                  total: jobs.length,
                })}
              </Typography>
            </Stack>

            <Tabs
              value={categoryFilter}
              onChange={(_, value) => setCategoryFilter(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 36,
                '& .MuiTab-root': {
                  minHeight: 36,
                  py: 0.5,
                  px: 1.25,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 12.5,
                  color: colors.gray[600],
                  '&.Mui-selected': { color: status.info.text },
                },
                '& .MuiTabs-indicator': { backgroundColor: status.info.main, height: 2 },
              }}
            >
              <Tab
                value="all"
                label={
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <span>{t('automation.jobs.tab.all', 'All')}</span>
                    <Badge
                      badgeContent={jobs.length}
                      max={999}
                      sx={{
                        '& .MuiBadge-badge': {
                          position: 'static',
                          transform: 'none',
                          bgcolor: categoryFilter === 'all' ? status.info.bg : colors.gray[200],
                          color: categoryFilter === 'all' ? status.info.text : colors.gray[700],
                          fontWeight: 700,
                          fontSize: 10.5,
                          height: 18,
                          minWidth: 22,
                        },
                      }}
                    />
                  </Stack>
                }
              />
              {categoryGroups.map((group) => (
                <Tab
                  key={group.key}
                  value={group.key}
                  label={
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <span>{group.label}</span>
                      <Badge
                        badgeContent={group.count}
                        max={999}
                        sx={{
                          '& .MuiBadge-badge': {
                            position: 'static',
                            transform: 'none',
                            bgcolor: categoryFilter === group.key ? status.info.bg : colors.gray[200],
                            color: categoryFilter === group.key ? status.info.text : colors.gray[700],
                            fontWeight: 700,
                            fontSize: 10.5,
                            height: 18,
                            minWidth: 22,
                          },
                        }}
                      />
                    </Stack>
                  }
                />
              ))}
            </Tabs>
          </Box>

          <AutomationJobsTable
            jobs={filteredJobs}
            onTrigger={(jobKey) => jobMutation.mutate(jobKey)}
            pendingJobKey={jobMutation.isPending ? jobMutation.variables : null}
            t={t}
          />
        </Paper>
      </Stack>
    </Box>
  );
}

export default AutomationCenter;

